import type { ParentMessage } from "@spork/s4p-server-sdk";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    FlatList,
    Platform,
    Pressable,
    View,
} from "react-native";
import Swipeable from "react-native-gesture-handler/Swipeable";
import { SafeAreaView } from "react-native-safe-area-context";

import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { formatDisplayDateTime } from "@/lib/date";
import { navigateFromNotificationData } from "@/lib/pushNotifications";
import { cn } from "@/lib/utils";
import { log } from "@/logger";
import {
    useDeleteParentMessageMutation,
    useGetParentMessagesQuery,
    useMarkParentMessageReadMutation,
} from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

type NotificationRowProps = {
    item: ParentMessage;
    onOpen: (message: ParentMessage) => void;
    onDelete: (id: number) => void;
};

/**
 * One inbox row. A left swipe deletes it. On web a delete button does the same.
 */
function NotificationRow({ item, onOpen, onDelete }: NotificationRowProps) {
    const { t } = useTranslation();

    const content = (
        <Pressable
            accessibilityRole="button"
            accessibilityActions={[
                { name: "delete", label: t("notifications.delete") },
            ]}
            onAccessibilityAction={(event) => {
                if (event.nativeEvent.actionName === "delete") {
                    onDelete(item.id);
                }
            }}
            className={cn(
                "min-w-0 flex-row gap-3 bg-card px-4 py-4 active:bg-accent",
                Platform.OS === "web" && "flex-1",
            )}
            onPress={() => {
                onOpen(item);
            }}
            testID={TEST_IDS[TEST_IDS.NOTIFICATIONS_LIST_ITEM]}
        >
            <View
                className={cn(
                    "mt-1.5 h-2 w-2 rounded-full",
                    item.read_at ? "bg-transparent" : "bg-primary",
                )}
                testID={
                    item.read_at
                        ? undefined
                        : TEST_IDS[TEST_IDS.NOTIFICATIONS_UNREAD_DOT]
                }
            />
            <View className="min-w-0 flex-1">
                <Text className={item.read_at ? undefined : "font-sans-medium"}>
                    {item.title}
                </Text>
                <Text variant="muted" numberOfLines={3}>
                    {item.body}
                </Text>
                <Text variant="caption" className="mt-2 text-xs">
                    {formatDisplayDateTime(item.created_at)}
                </Text>
            </View>
        </Pressable>
    );

    if (Platform.OS === "web") {
        return (
            <View className="flex-row items-center border-b border-border bg-card sm:w-[500px] w-lg active:bg-accent">
                {content}
                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t("notifications.delete")}
                    className="px-4 py-4 active:opacity-70"
                    onPress={() => {
                        onDelete(item.id);
                    }}
                    testID={TEST_IDS[TEST_IDS.NOTIFICATIONS_DELETE]}
                >
                    <Icon
                        name="trash-can"
                        className="text-muted-foreground"
                        size={16}
                    />
                </Pressable>
            </View>
        );
    }

    return (
        <Swipeable
            overshootRight={false}
            renderRightActions={() => (
                <View
                    className="h-full w-24 items-center justify-center bg-destructive"
                    testID={TEST_IDS[TEST_IDS.NOTIFICATIONS_DELETE]}
                >
                    <Text className="text-white">
                        {t("notifications.delete")}
                    </Text>
                </View>
            )}
            onSwipeableOpen={() => {
                onDelete(item.id);
            }}
        >
            <View className="border-b border-border bg-card">{content}</View>
        </Swipeable>
    );
}

/**
 * Inbox of notifications stored on the server.
 * Shows the last 7 days. One action extends that to 21 days, at most 100 items.
 * Tapping one marks it read and opens its screen. Swiping left deletes it.
 */
export function NotificationsScreen() {
    const { t } = useTranslation();
    const [extended, setExtended] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
    const week = useGetParentMessagesQuery(7);
    const older = useGetParentMessagesQuery(21, { skip: !extended });
    const [markRead] = useMarkParentMessageReadMutation();
    const [deleteMessage] = useDeleteParentMessageMutation();

    const showingOlder = Boolean(extended && older.data);
    const active = showingOlder ? older : week;
    const messages = (active.data?.data ?? []).filter(
        (message) => !pendingDeleteIds.includes(message.id),
    );
    const loadingOlder = extended && older.isFetching && !older.data;
    const hasMore = !showingOlder && (week.data?.has_more ?? false);
    const isLoading = week.isLoading && !week.data;
    const isError = !active.data && (extended ? older.isError : week.isError);

    const onRefreshData = useCallback(() => {
        const jobs = [week.refetch()];
        if (extended) {
            jobs.push(older.refetch());
        }
        return Promise.all(jobs);
    }, [extended, older, week]);
    const { refreshControl } = usePullToRefresh(onRefreshData);

    async function openMessage(message: ParentMessage) {
        if (!message.read_at) {
            try {
                await markRead(message.id).unwrap();
            } catch (error) {
                log.warn("Failed to mark notification as read", error);
            }
        }
        navigateFromNotificationData(message.data ?? undefined);
    }

    function deleteNotification(id: number) {
        setPendingDeleteIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
        void deleteMessage(id)
            .unwrap()
            .catch((error: unknown) => {
                setPendingDeleteIds((ids) =>
                    ids.filter((pendingId) => pendingId !== id),
                );
                log.warn("Failed to delete notification", error);
            });
    }

    return (
        <SafeAreaView
            className="flex-1 bg-background web:mx-auto"
            edges={["bottom"]}
        >
            <Text variant="caption" className="px-6 py-2">
                {Platform.OS === "web"
                    ? t("notifications.descriptionWeb")
                    : t("notifications.descriptionMobile")}
            </Text>
            {isLoading ? (
                <View className="flex-1 items-center justify-center">
                    <ActivityIndicator />
                </View>
            ) : (
                <View className="flex-1">
                    <View className="mx-4 mb-4 flex-1 overflow-hidden rounded-card border border-border bg-card">
                        <FlatList
                            className="flex-1"
                            data={messages}
                            extraData={pendingDeleteIds}
                            keyExtractor={(message) => String(message.id)}
                            refreshControl={refreshControl}
                            testID={TEST_IDS[TEST_IDS.NOTIFICATIONS_LIST]}
                            contentContainerClassName={
                                messages.length === 0 &&
                                !hasMore &&
                                !loadingOlder
                                    ? "grow"
                                    : undefined
                            }
                            ListEmptyComponent={
                                hasMore || loadingOlder ? null : (
                                    <View className="flex-1 items-center justify-center gap-2 px-6">
                                        <Text variant="label">
                                            {isError
                                                ? t("notifications.loadError")
                                                : t("notifications.emptyTitle")}
                                        </Text>
                                        {!isError && (
                                            <Text
                                                variant="muted"
                                                className="text-center"
                                            >
                                                {t(
                                                    "notifications.emptyDescription",
                                                )}
                                            </Text>
                                        )}
                                    </View>
                                )
                            }
                            ListFooterComponent={
                                hasMore || loadingOlder ? (
                                    <Pressable
                                        accessibilityRole="button"
                                        className={cn(
                                            "items-center px-4 py-4 active:opacity-70",
                                            loadingOlder && "opacity-50",
                                        )}
                                        disabled={loadingOlder}
                                        onPress={() => {
                                            if (extended) {
                                                void older.refetch();
                                                return;
                                            }
                                            setExtended(true);
                                        }}
                                        testID={
                                            TEST_IDS[
                                                TEST_IDS
                                                    .NOTIFICATIONS_LOAD_OLDER
                                            ]
                                        }
                                    >
                                        {loadingOlder ? (
                                            <ActivityIndicator />
                                        ) : (
                                            <Text variant="muted">
                                                {t("notifications.loadOlder")}
                                            </Text>
                                        )}
                                    </Pressable>
                                ) : null
                            }
                            renderItem={({ item }) => (
                                <NotificationRow
                                    item={item}
                                    onOpen={(message) => {
                                        void openMessage(message);
                                    }}
                                    onDelete={deleteNotification}
                                />
                            )}
                        />
                    </View>
                </View>
            )}
        </SafeAreaView>
    );
}
